import { roleApi } from '../../api/roleApi'

import { addNotification, notifyException, NotificationTypes } from './notificationActions'

import { organizationRoleListSignal, organizationRoleListErrorSignal, organizationRoleListLoadedSignal, roleErrorSignal, roleLoadedSignal, roleSignal, rolePermissionsSignal, rolePermissionsLoadedSignal, rolePermissionsErrorSignal, ephemeralRoleSignal, ephemeralRoleLoadedSignal, ephemeralRoleErrorSignal, roleBusinessEntitiesSignal, roleBusinessEntitiesLoadedSignal, roleBusinessEntitiesErrorSignal, organizationRoleDictionarySignal, canGrantRoleListSignal, canGrantRoleListLoadedSignal, canGrantRoleListErrorSignal } from '../signals/roleSignals.js'

export const addRole = async (businessEntityId, role, config, accessToken) => {
    organizationRoleListLoadedSignal.value = true
    organizationRoleListErrorSignal.value = null
    return await roleApi.addRole(businessEntityId, role, config, accessToken).then(
        async (newRole) => {
            return await fetchOrganizationRoleList(businessEntityId, config, accessToken).then(
                () => {
                    addNotification(`Successfully added new role ${role.name}`, NotificationTypes.SUCCESS, "Added role")
                    organizationRoleListLoadedSignal.value = true
                    return newRole
                }
            )
        }
    ).catch(
        (error) => {
            notifyException(error, "Add new role failure")
            organizationRoleListErrorSignal.value = error
            throw error
        }
    )
}

export const fetchEphemeralRole = async (businessEntityId, config, accessToken) => {
    if (ephemeralRoleErrorSignal.value) {
        console.error(ephemeralRoleErrorSignal.value)
        return null
    }
    ephemeralRoleLoadedSignal.value = false
    return await roleApi.fetchEphemeralRole(businessEntityId, config, accessToken).then(
        (ephemeralRole) => {
            ephemeralRoleLoadedSignal.value = true
            return ephemeralRoleSignal.value = ephemeralRole
        }
    ).catch(
        (error) => {
            ephemeralRoleErrorSignal.value = error
            throw error
        }
    )
}

export const fetchRole = async (roleId, config, accessToken) => {
    roleLoadedSignal.value = false
    return await roleApi.fetchRole(roleId, config, accessToken).then(
        (role) => {
            roleSignal.value = role
            roleErrorSignal.value = null
            roleLoadedSignal.value = true
            return role
        }
    ).catch(
        (error) => {
            notifyException(error, "Error fetching role ".concat(roleId))
            roleErrorSignal.value = error
            throw error
        }
    )
}

export const fetchUserRoleBusinessEntities = async (businessEntityId, config, accessToken) => {
    roleBusinessEntitiesLoadedSignal.value = false
    roleBusinessEntitiesSignal.value = []
    return await roleApi.fetchUserRoleBusinessEntities(businessEntityId, config, accessToken).then(
        (roleBusinessEntities) => {
            roleBusinessEntities.value = roleBusinessEntities
            roleBusinessEntitiesErrorSignal.value = null
            roleBusinessEntitiesLoadedSignal.value = true
            return roleBusinessEntities
        }
    ).catch(
        (error) => {
            roleBusinessEntitiesErrorSignal.value = error
        }
    )
}

export const fetchOrganizationRoleList = async (businessEntityId, config, accessToken, useDictionary=false) => {
    const f = "fetchOrganizationRoleList"
    let print = true
    organizationRoleListLoadedSignal.value = false
    organizationRoleListSignal.value = []
    organizationRoleDictionarySignal.value = {}
    return await roleApi.fetchOrganizationRoleList(businessEntityId, config, accessToken).then(
        (roles) => {
            for (let i = 0; i < roles.length; i++) {
                const role = roles[i]
                role.userCount = 0
                if ("properties" in role) {
                    for (let j = 0; j < role.properties.length; j++) {
                        const property = role.properties[j]
                        switch (property.key) {
                            case 'Users':
                                role.userCount = parseInt(property.value)
                                break
                            default:
                                window.alert(`${f}: Unhandled role property ${property.key}`)
                                break
                        }
                    }
                }
                else if (print) {
                    console.log(`${f}: Role #${i} does not have any properties`)
                }
            }
            organizationRoleListSignal.value = roles
            if (useDictionary) {
                let dict = {}
                for (let i = 0; i < roles.length; i++) {
                    const role = roles[i]
                    //console.log("Role: ")
                    //console.log(role)
                    dict[role.id] = role
                }
                organizationRoleDictionarySignal.value = dict
            }
            organizationRoleListLoadedSignal.value = true
            return roles
        }
    ).catch(
        (error) => {
            organizationRoleListErrorSignal.value = error
            notifyException(error, "Error fetching role list for organization with ID ".concat(businessEntityId))
        }
    )
}

export const deleteRole = async (businessEntityId, role, config, accessToken) => {
    roleLoadedSignal.value = false
    roleErrorSignal.value = null
    return await roleApi.deleteRole(role.id, config, accessToken).then(
        async (response) => {
            roleErrorSignal.value = null
            return await fetchOrganizationRoleList(businessEntityId, config, accessToken).finally(
                () => {
                    addNotification(`Successfully deleted role ${role.name}`, NotificationTypes.SUCCESS, "Deletion successful")
                    return response
                }
            )
        }
    ).catch(
        (error) => {
            roleErrorSignal.value = error
            notifyException(error, "Deletion failed")
        }
    )
}

export const fetchRolePermissions = async (roleId, config, accessToken) => {
    rolePermissionsLoadedSignal.value = false
    rolePermissionsSignal.value = []
    return await roleApi.fetchRolePermissions(roleId, config, accessToken).then(
        (permissions) => {
            rolePermissionsErrorSignal.value = null
            rolePermissionsSignal.value = permissions
            permissions.sort(
                (a, b) => {
                    if (a.name.toLowerCase() > b.name.toLowerCase()) {
                        return 1
                    }
                    return -1
                }
            )
            rolePermissionsLoadedSignal.value = true
            return permissions
        }
    ).catch(
        (error) => {
            rolePermissionsErrorSignal.value = error
            notifyException(error, "Error fetching role permissions")
        }
    )
}

export const fetchCanGrantRolesList = async (businessEntityId, config, accessToken) => {
    canGrantRoleListLoadedSignal.value = false
    canGrantRoleListSignal.value = []
    return await roleApi.fetchCanGrantRoleList(businessEntityId, config, accessToken).then(
        (canGrantRoles) => {
            canGrantRoleListSignal.value = canGrantRoles
            canGrantRoleListLoadedSignal.value = true
            return canGrantRoles

        }
    ).catch(
        (err) => {
            canGrantRoleListErrorSignal.value = err.message
            throw err
        }
    )
}